import {getSize, useResponsivePropValue} from 'platform/foundation';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CSS_ALL_DAY_EVENT_HEIGHT} from '../constants/cssAllDayEventHeight';
import {DAY_COLUMN_TEMPLATE} from '../constants/dayColumnTemplate';
import {DAYS_IN_WEEK} from '../constants/daysInWeek';
import {EVENT_ROW_GAP} from '../constants/eventRowGap';
import type {BigCalendarEvent} from '../types/BigCalendarEvent';
import type {EventSegment} from '../types/EventSegment';
import {AllDayEventItem} from './AllDayEventItem';

interface AllDayEventsGridProps extends RequiredTestIdProps {
  segments: EventSegment[];
  onEventClick?: (event: BigCalendarEvent) => void;
  enableHover?: boolean;
  onEventMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onEventMouseLeave?: (event: BigCalendarEvent) => void;
}

/**
 * The all-day header of the week view. Every segment is one element spanning all of its day
 * columns on the row the packer assigned, so a multi-day event reads as one continuous bar.
 */
export function AllDayEventsGrid(props: AllDayEventsGridProps) {
  const dayColumns = useResponsivePropValue(DAY_COLUMN_TEMPLATE);

  const rowCount = props.segments.reduce((max, segment) => Math.max(max, segment.slot + 1), 0);

  return (
    <div
      data-testid={suffixTestId('allDayEvents', props)}
      className="grid min-w-0 border-b border-[var(--color-general-separator)] py-1"
      style={{
        gridTemplateColumns: dayColumns.template,
        gridAutoRows: CSS_ALL_DAY_EVENT_HEIGHT,
        rowGap: EVENT_ROW_GAP,
      }}
    >
      {DAY_COLUMNS.map((column) => (
        <div
          key={column}
          className="pointer-events-none border-l border-[var(--color-general-separator)]"
          style={{gridColumn: column + 1, gridRow: `1 / span ${rowCount}`}}
        />
      ))}

      {props.segments.map((segment, segmentIndex) => (
        <div
          key={segment.event.id}
          className="min-w-0"
          style={{
            gridColumn: `${segment.columnStart + 1} / span ${segment.columnSpan}`,
            gridRow: segment.slot + 1,
            paddingLeft: segment.isStartClipped ? 0 : getSize(1),
            paddingRight: segment.isEndClipped ? 0 : getSize(1),
          }}
        >
          <AllDayEventItem
            data-testid={suffixTestId(`allDayEvents-[${segmentIndex}]`, props)}
            event={segment.event}
            isStartClipped={segment.isStartClipped}
            isEndClipped={segment.isEndClipped}
            onClick={props.onEventClick}
            enableHover={props.enableHover}
            onMouseEnter={props.onEventMouseEnter}
            onMouseLeave={props.onEventMouseLeave}
          />
        </div>
      ))}
    </div>
  );
}

const DAY_COLUMNS = Array.from({length: DAYS_IN_WEEK}, (_, column) => column);
